import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import GradientButton from "./ui/GradientButton";
import MosqueSkyline from "./ui/MosqueSkyline";
import kaaba from "../images/kaaba.jpg";

const reduceMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const stats = [
  ["700K+", "Happy Customers"],
  ["350K+", "Packages"],
  ["21+", "Years Trusted"],
];

const Hero = () => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef(null);

  // scroll-linked parallax (disabled when reduced motion is preferred)
  const { scrollY } = useScroll();
  const sceneY = useTransform(scrollY, [0, 700], reduceMotion ? [0, 0] : [0, -90]);
  const copyY = useTransform(scrollY, [0, 700], reduceMotion ? [0, 0] : [0, 50]);
  const skylineY = useTransform(scrollY, [0, 700], reduceMotion ? [0, 0] : [0, -30]);

  const onMove = (e) => {
    if (reduceMotion || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -6, y: px * 6 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className="relative flex min-h-screen items-center overflow-hidden section-pad pt-28 bg-[radial-gradient(130%_120%_at_78%_-10%,#1c6b30_0%,#0B3D1B_50%,#072414_100%)]"
    >
      {/* islamic geometric star pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Cg fill='none' stroke='%23FFC107' stroke-width='1'%3E%3Crect x='16' y='16' width='32' height='32'/%3E%3Cpath d='M32 8 L56 32 L32 56 L8 32 Z'/%3E%3C/g%3E%3C/svg%3E\")",
          backgroundSize: "64px 64px",
        }}
      />

      {/* animated color mesh */}
      <div className="blob h-[520px] w-[520px] -right-32 -top-40 bg-brand-green/60 animate-float" />
      <div className="blob h-[380px] w-[380px] -left-24 bottom-[-120px] bg-brand-orange/40 animate-float-slow" />
      <div className="blob h-[280px] w-[280px] right-[26%] top-[36%] bg-brand-gold/25 animate-float" />

      {/* twinkling stars */}
      <span className="absolute left-[18%] top-[22%] h-1.5 w-1.5 rounded-full bg-brand-gold/80 animate-pulse" />
      <span className="absolute left-[40%] top-[14%] h-1 w-1 rounded-full bg-white/70 animate-pulse [animation-delay:0.6s]" />
      <span className="absolute right-[12%] top-[28%] h-1.5 w-1.5 rounded-full bg-brand-gold/70 animate-pulse [animation-delay:1.1s]" />
      <span className="absolute left-[33%] top-[42%] h-1 w-1 rounded-full bg-white/60 animate-pulse [animation-delay:1.6s]" />

      {/* mosque skyline silhouette */}
      <motion.div
        style={{ y: skylineY }}
        className="pointer-events-none absolute bottom-0 left-0 w-full"
      >
        <MosqueSkyline className="h-40 w-full text-[#06200f]/70 sm:h-48" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-[1240px] items-center gap-12 lg:grid-cols-2">
        {/* Copy */}
        <motion.div style={{ y: copyY }} className="text-center lg:text-left">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
          >
            <span className="h-2 w-2 rounded-full bg-brand-gold shadow-[0_0_10px_#FFC107]" />
            Trusted Hajj &amp; Umrah Partner
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Your Sacred Journey,
            <br />
            <span className="text-gradient">Our Commitment</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/80 lg:mx-0"
          >
            Experience Makkah &amp; Madina with premium, hassle-free packages —
            visas, flights, 5-star stays and guided Ziyarat, all handled with care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start"
          >
            <GradientButton variant="orange" href="#hajjumrah">
              Explore Packages
            </GradientButton>
            <GradientButton variant="ghost" href="#gallery">
              ▶ Watch Tour
            </GradientButton>
          </motion.div>

          <div className="mt-11 flex justify-center gap-6 sm:gap-8 lg:justify-start">
            {stats.map(([num, label]) => (
              <div key={label}>
                <div className="text-3xl font-extrabold text-gradient">{num}</div>
                <div className="text-xs text-white/60">{label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Kaaba arch scene */}
        <motion.div
          style={{ y: sceneY }}
          className="relative flex min-h-[26rem] items-center justify-center [perspective:1200px] sm:min-h-[34rem]"
        >
          {/* glow halos */}
          <div className="absolute h-[30rem] w-[22rem] rounded-full bg-[radial-gradient(circle,rgba(255,193,7,0.45),transparent_68%)] blur-2xl" />
          <div className="absolute bottom-10 h-40 w-80 rounded-full bg-[radial-gradient(circle,rgba(76,175,24,0.4),transparent_70%)] blur-2xl" />

          {/* orbit rings */}
          <div className="absolute h-[26rem] w-[26rem] rounded-full border border-dashed border-brand-gold/30 animate-spin-slow [transform:rotateX(74deg)]" />
          <div className="absolute h-[20rem] w-[20rem] rounded-full border border-dashed border-brand-orange/30 animate-spin-slow [animation-direction:reverse] [transform:rotateX(74deg)]" />

          <motion.div
            animate={reduceMotion ? {} : { y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
            className="relative transition-transform duration-200 ease-out [transform-style:preserve-3d]"
          >
            {/* mihrab arch frame with gold border */}
            <div className="rounded-t-[150px] rounded-b-[2rem] bg-gradient-to-b from-brand-gold via-brand-orange/80 to-brand-gold p-[3px] shadow-[0_30px_70px_rgba(0,0,0,0.5)]">
              <div className="relative h-[22rem] w-64 overflow-hidden rounded-t-[148px] rounded-b-[1.85rem] sm:h-[27rem] sm:w-80">
                <img
                  src={kaaba}
                  alt="The Holy Kaaba, Masjid al-Haram, Makkah"
                  className="h-full w-full object-cover object-[center_30%]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/55 via-transparent to-brand-dark/10" />
                <div className="pointer-events-none absolute inset-0 rounded-t-[148px] rounded-b-[1.85rem] ring-1 ring-inset ring-white/15" />
              </div>
            </div>

            {/* keystone */}
            <span className="absolute -top-1.5 left-1/2 h-3.5 w-3.5 -translate-x-1/2 rounded-full bg-brand-gold shadow-[0_0_14px_#FFC107]" />
          </motion.div>

          <motion.span
            aria-hidden
            className="pointer-events-none absolute left-0 top-1/2 text-2xl text-white/85"
            initial={{ x: -80, y: 110, opacity: 0, rotate: 18 }}
            animate={
              reduceMotion
                ? { x: 60, y: -80, opacity: 0.85, rotate: 18 }
                : {
                    x: [-80, 130, 360, 600],
                    y: [110, 20, -90, -230],
                    opacity: [0, 1, 1, 0],
                    rotate: 18,
                  }
            }
            transition={
              reduceMotion
                ? {}
                : { duration: 11, repeat: Infinity, ease: "linear", repeatDelay: 1 }
            }
          >
            ✈
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
